import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { useSearchScope } from '../hooks/useSearchScope';
import { useSearchFilters, DEFAULT_SORT } from '../hooks/useSearchFilters';
import { useVenueMapPins } from '../hooks/useVenueMapPins';
import { PageContainer } from '../components/PageContainer';
import {
  CategoryChips,
  MapPanel,
  SearchAreaButton,
  SearchHeader,
  ResultsHeader,
  ResultsList,
  MapExpandToggle,
  MobileMapOverlay,
} from '../components/SearchPage';
import type { SortOption } from '../components/SearchPage';
import { SearchResultsWithMap } from '../components/SearchResultsWithMap';
import type { DistanceLocale } from '@wineryxplorer/shared';
import type { SearchState } from '@wineryxplorer/shared';

const PAGE_SIZE = 20;

export const SearchPage = () => {
  const navigate = useNavigate();

  // --- Scope state from hook ---
  const {
    selectedRegion,
    selectedCity,
    userLocation,
    mapBounds,
    pendingMapBounds,
    isLocating,
    showGeoPrompt,
    geoError,
    locationParam,
    boundsParam,
    isMapAreaMode,
    hasUncommittedPan,
    scopePill,
    initialMapBounds,
    mapCenter,
    mapZoom,
    handleRegionChange,
    handleCitySearch,
    handleUseMyLocation,
    handleMapBoundsChanged,
    handleCommitPendingBounds,
    handleClearMapArea,
    setShowGeoPrompt,
  } = useSearchScope();

  // --- Page-only URL-synced filter state ---
  const {
    searchText,
    setSearchText,
    venueTypes,
    atmospheres,
    amenities,
    sortOption,
    setSortOption,
    currentPage,
    setCurrentPage,
    showMobileMap,
    setShowMobileMap,
    handleToggleVenueType,
    handleToggleAtmosphere,
    handleToggleAmenity,
    handleClearFilters,
    hasActiveFilters,
  } = useSearchFilters();

  // Map UI-only state (not scope)
  const [highlightedVenueId, setHighlightedVenueId] = useState<string | null>(null);
  const [expandedMap, setExpandedMap] = useState(false);

  // User distance-unit preference (authenticated users only)
  const { isAuthenticated } = useAuth();
  const { data: profileData } = trpc.user.getMe.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const userDistanceUnit = profileData?.distanceUnit;

  // Resolve distance locale: user preference > geolocation heuristic > imperial default
  const distanceLocale: DistanceLocale = useMemo(() => {
    if (userDistanceUnit) return userDistanceUnit === 'km' ? 'metric' : 'imperial';
    if (!userLocation) return 'imperial'; // Default to imperial (US-focused app)
    // Simple heuristic: continental US bounding box
    const { lat, lng } = userLocation;
    const isUS = lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66;
    return isUS ? 'imperial' : 'metric';
  }, [userDistanceUnit, userLocation]);

  // --- Page-level reset effects ---
  // Reset to page 1 + distance sort when the user genuinely changes region or city.
  // Uses a previous-value ref so StrictMode double-invocation doesn't spuriously
  // reset on mount (initial render's effect fires twice; both see the same value).
  const prevScopeIdRef = useRef<string>(
    `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}`
  );
  useEffect(() => {
    const currentId = `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}`;
    if (prevScopeIdRef.current !== currentId) {
      prevScopeIdRef.current = currentId;
      setCurrentPage(1);
      setSortOption('distance');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, selectedCity]);

  // Reset to page 1 when committed map bounds genuinely change.
  const prevBoundsIdRef = useRef<string>(
    mapBounds ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}` : ''
  );
  useEffect(() => {
    const currentId = mapBounds
      ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}`
      : '';
    if (prevBoundsIdRef.current !== currentId) {
      prevBoundsIdRef.current = currentId;
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapBounds]);

  // --- Wrapped handleUseMyLocation with page resets ---
  const onUseMyLocation = useCallback(() => {
    handleUseMyLocation(() => {
      setCurrentPage(1);
      setSortOption('distance');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUseMyLocation]);

  const sortParams = useMemo(() => {
    // Distance sort requires location param (not just hasLocation, since bounds suppress it)
    const effectiveSort =
      sortOption === 'distance' && !locationParam ? 'name-asc' : sortOption;
    switch (effectiveSort) {
      case 'distance':
        return { sortBy: 'distance', sortOrder: 'asc' as const };
      case 'name-asc':
        return { sortBy: 'name', sortOrder: 'asc' as const };
      case 'name-desc':
        return { sortBy: 'name', sortOrder: 'desc' as const };
      default:
        return { sortBy: 'name', sortOrder: 'asc' as const };
    }
  }, [sortOption, locationParam]);

  const searchInput = useMemo(
    () => ({
      query: searchText || undefined,
      venueTypes: venueTypes.length > 0 ? venueTypes : undefined,
      priceRanges: undefined as number[] | undefined,
      primaryAtmospheres: atmospheres.length > 0 ? atmospheres : undefined,
      amenities: amenities.length > 0 ? amenities : undefined,
      location: locationParam,
      bounds: boundsParam,
      ...sortParams,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      fallbackCount: 5,
    }),
    [searchText, venueTypes, atmospheres, amenities, locationParam, boundsParam, sortParams, currentPage]
  );

  const { data, isFetching, error } = trpc.venue.search.useQuery(searchInput);
  // Use isFetching (not isPending) so the loading state shows on EVERY refetch
  // when the search input changes — otherwise stale data lingers when the user
  // pans/clears bounds and the cards visibly desync from the map state.
  const isPending = isFetching;

  // Map pins come from their own query so they track the live pending bounds.
  const mapVenues = useVenueMapPins({
    pendingMapBounds,
    initialMapBounds,
    venueTypes,
    atmospheres,
    amenities,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const handlePinClick = useCallback(
    (venueId: string) => {
      navigate(`/wineries/${venueId}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (s: SortOption) => {
      if (s === 'distance' && !userLocation) {
        // Need geolocation — request it, then set sort + page on success
        onUseMyLocation();
        return;
      }
      setSortOption(s);
      setCurrentPage(1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userLocation, onUseMyLocation]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Overlay nodes (built once per render, passed to all MapPanel instances) ---
  const searchAreaButton = hasUncommittedPan && pendingMapBounds ? (
    <SearchAreaButton
      bounds={pendingMapBounds}
      filters={{
        venueTypes: venueTypes.length > 0 ? venueTypes : undefined,
        primaryAtmospheres: atmospheres.length > 0 ? atmospheres : undefined,
        amenities: amenities.length > 0 ? amenities : undefined,
      }}
      onCommit={handleCommitPendingBounds}
    />
  ) : null;

  // Map area is now displayed as a pill in the Where? input field instead of
  // a separate map overlay, so this stays null. (See the Where field render below.)
  const mapAreaPill = null;

  // Every MapPanel instance (sidebar, expanded, mobile overlay) takes the same props.
  const mapPanel = (
    <MapPanel
      venues={mapVenues}
      onBoundsChanged={handleMapBoundsChanged}
      highlightedVenueId={highlightedVenueId}
      onPinClick={handlePinClick}
      center={mapCenter}
      zoom={mapZoom}
      topCenterOverlay={searchAreaButton}
      topRightOverlay={mapAreaPill}
    />
  );

  // Snapshot the current search as a SearchState for SavedSearchesMenu. The
  // serializer (utils/searchStateSerializer.ts) enforces the Option B mutual
  // exclusion: if bounds are present, region/city are stripped at URL build.
  const currentSearchState: SearchState = useMemo(
    () => ({
      q: searchText || undefined,
      region: selectedRegion?.slug,
      city: selectedCity?.name,
      cityLat: selectedCity?.lat,
      cityLng: selectedCity?.lng,
      bounds: mapBounds
        ? {
            n: mapBounds.ne.lat,
            s: mapBounds.sw.lat,
            e: mapBounds.ne.lng,
            w: mapBounds.sw.lng,
          }
        : undefined,
      venueTypes: venueTypes.length > 0 ? venueTypes : undefined,
      atmospheres: atmospheres.length > 0 ? atmospheres : undefined,
      amenities: amenities.length > 0 ? amenities : undefined,
      sort: sortOption !== DEFAULT_SORT ? sortOption : undefined,
    }),
    [
      searchText,
      selectedRegion,
      selectedCity,
      mapBounds,
      venueTypes,
      atmospheres,
      amenities,
      sortOption,
    ]
  );

  // Auto-suggest a save name based on what the user is currently looking at.
  const defaultSaveName = useMemo(() => {
    if (selectedRegion) return selectedRegion.name;
    if (selectedCity) return selectedCity.name;
    if (mapBounds) return 'Map area';
    const filterParts = [searchText, ...venueTypes, ...atmospheres, ...amenities].filter(Boolean);
    return filterParts.length > 0 ? filterParts.join(' · ').slice(0, 80) : 'Untitled search';
  }, [selectedRegion, selectedCity, mapBounds, searchText, venueTypes, atmospheres, amenities]);

  // --- Render ---
  return (
    <PageContainer>
      <div className="space-y-4">
        {/* Search bar section */}
        <SearchHeader
          currentSearchState={currentSearchState}
          currentVenueCount={data?.total ?? 0}
          defaultSaveName={defaultSaveName}
          showGeoPrompt={showGeoPrompt}
          isLocating={isLocating}
          onUseMyLocation={onUseMyLocation}
          onDismissGeoPrompt={() => {
            setShowGeoPrompt(false);
            setSortOption('name-asc');
          }}
          scopePill={scopePill}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          isMapAreaMode={isMapAreaMode}
          onClearMapArea={handleClearMapArea}
          selectedRegion={selectedRegion}
          selectedCity={selectedCity}
          onRegionChange={handleRegionChange}
          onCitySearch={handleCitySearch}
          geoError={geoError}
        />

        {/* Category chips */}
        <CategoryChips
          selectedVenueTypes={venueTypes}
          selectedAtmospheres={atmospheres}
          selectedAmenities={amenities}
          onToggleVenueType={handleToggleVenueType}
          onToggleAtmosphere={handleToggleAtmosphere}
          onToggleAmenity={handleToggleAmenity}
        />

        {/* Results header */}
        <ResultsHeader
          isPending={isPending}
          total={data?.total}
          isMapAreaMode={isMapAreaMode}
          regionName={selectedRegion?.name}
          cityName={selectedCity?.name}
          hasUserLocation={!!userLocation}
          hasActiveFilters={hasActiveFilters}
          searchText={searchText}
          venueTypes={venueTypes}
          atmospheres={atmospheres}
          amenities={amenities}
          onClearFilters={handleClearFilters}
          mapBounds={mapBounds}
          onShowMobileMap={() => setShowMobileMap(true)}
          sortOption={sortOption}
          onSortChange={handleSortChange}
        />

        {/* Main content: results list + map panel */}
        <SearchResultsWithMap
          stacked={expandedMap}
          map={mapPanel}
          mapFooter={<MapExpandToggle expanded={expandedMap} onToggle={setExpandedMap} />}
        >
          <ResultsList
            isPending={isPending}
            errorMessage={error?.message}
            data={data}
            searchText={searchText}
            highlightedVenueId={highlightedVenueId}
            onHighlightVenue={setHighlightedVenueId}
            userLocation={userLocation}
            distanceLocale={distanceLocale}
            scopePillLabel={scopePill?.label}
            hasActiveFilters={hasActiveFilters}
            isMapAreaMode={isMapAreaMode}
            onClearFilters={handleClearFilters}
            onClearMapArea={handleClearMapArea}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </SearchResultsWithMap>
      </div>

      {/* Mobile full-screen map overlay */}
      {showMobileMap && (
        <MobileMapOverlay
          map={mapPanel}
          onClose={() => setShowMobileMap(false)}
          venues={data?.items}
          highlightedVenueId={highlightedVenueId}
          userLocation={userLocation}
          distanceLocale={distanceLocale}
          isPending={isPending}
        />
      )}
    </PageContainer>
  );
};
